import { ViewPart } from "@kuika/kuika-cl-model";
import { ChangeEvent } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./searchable-panel-header.scss";

interface SearchablePanelHeaderProps {
  searchText: string;
  onSearchTextChange: (searchText: string) => void;
  onSearchClearCallback?: (e?: any) => void;
  viewPart: ViewPart;
  panelName: string | React.ReactNode;
  isSearchActive: boolean;
  setSearchActive: (isSearchActive: boolean) => void;
  extraRightUnit?: React.ReactNode;
  /**
   * The search control ids are generic across every searchable panel — a caller that an
   * existing suite addresses by a panel-specific name (Helpdesk's ticket search) overrides
   * them here rather than reaching past the primitive.
   */
  searchInputTestId?: string;
  searchButtonTestId?: string;
}

const SearchablePanelHeader: React.FC<SearchablePanelHeaderProps & IWithSDProps> = ({
  searchText,
  onSearchTextChange,
  onSearchClearCallback,
  viewPart,
  panelName,
  sd,
  isSearchActive = false,
  setSearchActive,
  extraRightUnit,
  searchInputTestId = "searchInput",
  searchButtonTestId = "searchButton"
}) => {
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newSearchText = e.target.value;
    onSearchTextChange(newSearchText);
    sd.setSearchTexts(viewPart, newSearchText);
  };

  const onSearchClear = (e: any) => {
    e.stopPropagation();
    const viewPartToClear = viewPart;

    sd.setSearchTexts(viewPartToClear, "");
    onSearchTextChange("");
    setSearchActive(false);
    if (onSearchClearCallback) {
      onSearchClearCallback(e);
    }
  };

  const onSearchActivate = () => {
    setSearchActive(true);
  };

  // Panel-scoped slug (e.g. "Screens" -> "screens") shared by the wrapper id,
  // onboarding id, and test id so a caller can target one specific panel's
  // search controls even though the inner searchInput/searchButton test ids are
  // generic across every searchable panel.
  const panelSlug: string = typeof panelName === "string" ? panelName.toLowerCase().replace(/\s+/g, "-") : "";

  return (
    <div
      className="searchable-panel-header"
      onClick={onSearchActivate}
      id={`searchable-panel-header-${panelSlug}`}
      data-testid={`searchablePanelHeader-${panelSlug}`}
      data-onboarding-id={`searchable-panel-header-${panelSlug}`}
    >
      {isSearchActive ? (
        <KuikaInput
          data-testid={searchInputTestId}
          placeholder="Search..."
          onChange={handleSearchInputChange}
          value={searchText}
          onFocus={(e) => e.target.select()}
          autoFocus
          borderless
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setSearchActive(false);
            }
          }}
        />
      ) : typeof panelName === "string" ? (
        <KuikaTextMedium
          data-onboarding-id={`searchable-panel-header-name-${panelName.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {panelName}
        </KuikaTextMedium>
      ) : (
        panelName
      )}
      <div className="kuika-flex--gap-xxs kuika-align-center">
        <div className="searchable-panel-header__icon kuika-flex-center">
          {isSearchActive ? (
            <KuikaButton
              data-testid="clearSearchInputButton"
              isIcon
              type="tertiary"
              color="neutral"
              onClick={onSearchClear}
              size="sm"
            >
              <KuikaSvg name="x" width={16} height={16} />
            </KuikaButton>
          ) : (
            <KuikaButton data-testid={searchButtonTestId} isIcon type="tertiary" color="neutral" size="sm">
              <KuikaSvg name="search" width={16} height={16} />
            </KuikaButton>
          )}
        </div>
        {!isSearchActive && (
          <div className="kuika-flex-center" onClick={(e) => e.stopPropagation()}>
            {extraRightUnit}
          </div>
        )}
      </div>
    </div>
  );
};

export default withSD<SearchablePanelHeaderProps>(SearchablePanelHeader);
